import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Chip, useMediaQuery } from "@mui/material";
import { IconBriefcase } from "@tabler/icons";
import ActionButton from "src/components/shared/ActionButton";
import { casesApi } from "src/pages/cases/api";
import { STATUS } from "src/pages/cases/format";

// Requests are the old, one-row-per-traveller view of applications. They use
// the Cases words and colours, and every request links to the case it
// belongs to: Cases is where applications are worked.

export function StatusChip({ status, ...rest }) {
  const s = STATUS[status] ?? { label: status || "Unknown", color: "default" };
  return <Chip size="small" color={s.color} label={s.label} {...rest} />;
}

StatusChip.propTypes = { status: PropTypes.string };

export function PaidChip({ isPaid, status, partner }) {
  // A draft has not reached payment yet: "Not paid" would read as a problem.
  if (status === "incomplete" && !Number(isPaid)) return null;
  const paid = Number(isPaid) === 1;
  // A partner's request is billed to the partner, never paid online.
  if (partner && !paid) return <Chip size="small" variant="outlined" color="info" label={`Billed to ${partner}`} />;
  return <Chip size="small" variant="outlined" color={paid ? "success" : "warning"} label={paid ? "Paid" : "Not paid"} />;
}

PaidChip.propTypes = {
  isPaid: PropTypes.oneOfType([PropTypes.number, PropTypes.bool, PropTypes.string]),
  status: PropTypes.string,
  partner: PropTypes.string,
};

// A case is opened by its main application id. Any traveller's request id
// resolves to it, so ask the workspace which case this request belongs to.
export async function caseIdFor(requestId, dispatch) {
  const response = await casesApi.get(requestId, dispatch);
  return response?.data?.id ?? Number(requestId);
}

// "Open case" from anywhere a request is shown. Busy while it looks the case
// up; if that fails it still opens the case page, which resolves the request
// itself and says so when it cannot.
export function OpenCaseButton({ requestId, caseId, label, ...rest }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  return (
    <ActionButton
      startIcon={<IconBriefcase size={16} />}
      busyLabel="Opening…"
      onClick={async (event) => {
        event.stopPropagation();
        let id = caseId;
        if (!id) {
          try {
            id = await caseIdFor(requestId, dispatch);
          } catch {
            id = requestId;
          }
        }
        navigate(`/cases/${id}`);
      }}
      {...rest}
    >
      {label || "Open case"}
    </ActionButton>
  );
}

OpenCaseButton.propTypes = {
  requestId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  caseId: PropTypes.number,
  label: PropTypes.node,
};

// Says plainly where the work happens. On a phone the link sits under the
// text, so the text keeps the banner's width.
export function CasesNote({ children, action }) {
  const navigate = useNavigate();
  const phone = useMediaQuery((t) => t.breakpoints.down("sm"));
  const link =
    action === undefined ? (
      <ActionButton variant="text" onClick={() => navigate("/cases")} sx={phone ? { ml: -1.5 } : undefined}>
        Go to Cases
      </ActionButton>
    ) : (
      action
    );
  return (
    <Alert
      severity="info"
      icon={<IconBriefcase size={20} />}
      sx={{ alignItems: phone ? "flex-start" : "center", mb: 2, "& .MuiAlert-action": { pt: 0 } }}
      action={phone ? undefined : link}
    >
      {children}
      {phone && link ? <Box sx={{ mt: 0.5 }}>{link}</Box> : null}
    </Alert>
  );
}

CasesNote.propTypes = { children: PropTypes.node, action: PropTypes.node };
